import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import { PROJECTS_SORT_BY_MAP } from "@components/projects/projects-sort-constants";
import { PROJECTS_TABLE_COLUMN_LABELS } from "@components/table/projects/projects-table-constants";
import { getSortByFromGridSortModel } from "@components/table/projects/projects-table-utils";
import { useProjectsTableColumns } from "@components/table/projects/use-projects-table-columns";
import { useProjectsTableContext } from "@components/table/projects/use-projects-table-context";
import { PaginatedProjects, SdbProject } from "@custom-types/project-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useHandleColumnOrderChange } from "@hooks/use-handle-column-order-change";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { useProjectsPageBulkActions } from "@pages/projects/use-projects-page-bulk-actions";
import { API_PROJECTS_PER_REQUEST } from "@store/projects/projects-slice-utils";
import { ProjectEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";

type Props = Pick<
  PaginatedProjects,
  | "companyId"
  | "projects"
  | "groupId"
  | "projectArchivingState"
  | "nextPageCursor"
  | "loadMoreProjects"
  | "sortBy"
  | "setSortBy"
  | "totalCount"
> & {
  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;
};

/**
 * Listing all the projects of a subject such as a company or a group in a table
 */
export function ProjectsTable({
  companyId,
  projects,
  isLoading,
  groupId,
  projectArchivingState,
  nextPageCursor,
  loadMoreProjects,
  sortBy,
  setSortBy,
  totalCount,
}: Props): JSX.Element {
  const { navigateToProjectDetail } = useAppNavigation();
  const { trackEvent } = useTrackEvent();
  const contextType = useProjectsTableContext({
    projectArchivingState,
    groupId,
  });

  const columns = useProjectsTableColumns({
    companyId,
    contextType,
  });

  // ##### Server side pagination, searching and sorting logic ##### //

  const sortModel = [PROJECTS_SORT_BY_MAP[sortBy]];

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getSortByFromGridSortModel(newModel);

      setSortBy(newSortBy);
    },
    [setSortBy]
  );

  // Callback fired when a project row is clicked
  const handleRowClick = useCallback(
    (project: SdbProject) => {
      trackEvent({
        name: ProjectEvents.openProjectDetails,
        props: {
          projectId: project.id,
          eventLocation: contextType,
          viewMode: "list",
        },
      });
      navigateToProjectDetail({
        companyId,
        projectId: project.id,
      });
    },
    [trackEvent, contextType, navigateToProjectDetail, companyId]
  );

  // Callback to load more projects when navigating forward
  const handleLoadMoreProjects = useCallback(async () => {
    return await loadMoreProjects();
  }, [loadMoreProjects]);

  // ##### Bulk actions logic ##### //

  // The available bulk actions to display according to the archiving status and table context.
  const actionButtons = useProjectsPageBulkActions({
    companyId,
    projects,
    projectStatus: projectArchivingState,
    subjectType: contextType === "group projects" ? "group" : "workspace",
  });

  // Map context type to TableType enum for FaroTable
  let tableTypeEnum: TableType;
  if (contextType === "active projects") {
    tableTypeEnum = TableType.activeProjects;
  } else if (contextType === "archived projects") {
    tableTypeEnum = TableType.archiveProjects;
  } else {
    tableTypeEnum = TableType.groupProjects;
  }

  /** Get the function to handle column order changes based on the table type */
  const handleColumnOrderChange = useHandleColumnOrderChange(tableTypeEnum);

  return (
    <FaroTable
      entities={projects}
      uniqueIdKey="id"
      isLoading={isLoading}
      columns={columns}
      onRowClick={handleRowClick}
      rowHeight={70}
      bulkActionButtons={actionButtons}
      pageSize={API_PROJECTS_PER_REQUEST}
      onNextPage={handleLoadMoreProjects}
      hasMoreItems={!!nextPageCursor}
      totalCount={totalCount}
      tableType={tableTypeEnum}
      onSortModelChange={handleSortModelChange}
      sortTrackingSubject="Projects"
      sortModel={sortModel}
      paginationMode="server"
      filterMenuTrigger={
        <FaroTableColumnVisibilityMenu
          labelsMap={PROJECTS_TABLE_COLUMN_LABELS}
          disabledColumns={["name"]}
          tableType={tableTypeEnum}
        />
      }
      onColumnOrderChange={handleColumnOrderChange}
      sx={{
        // Change cursor to pointer when hovering over table rows
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },

        "& .MuiDataGrid-columnHeader:hover .drag-handle": {
          display: "flex",
        },

        /** Fill all the header */
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
        },
      }}
    />
  );
}
